<script lang="ts" setup>
import getNumberWithSpaces from "~/composables/number-formatter";
import AppButton from "~/components/ui/buttons/AppButton.vue";
import Badge from "~/components/ui/badge/Badge.vue";
import PlugImage from "assets/icons/search/product-plug-unsized.svg";
import type { ICatalogProduct } from "~/shared/interfaces/catalog";

const props = withDefaults(
  defineProps<{
    product: ICatalogProduct;
    size?: "sm" | "m" | "l";
    withoutButton?: boolean;
  }>(),
  {
    size: "m",
  }
);

const isDisabledProduct = computed(() => props.product.price === 0);
</script>

<template>
  <NuxtLink
    class="product"
    :class="[
      withoutButton && 'product_withoutButton',
      size && `product_${size}`,
      isDisabledProduct && 'not-available',
    ]"
    :to="'/search/product/' + props.product.id"
  >
    <div class="product__container">
      <div class="product-content">
        <div class="product__inner-wrapper">
          <div class="product-img__wrapper">
            <nuxt-img
              v-if="product?.photoUrl"
              class="product-img"
              :src="product.photoUrl"
              :alt="
                product.title
                  ? `${product.title} - купить в интернет-магазине Parts.Sotrans`
                  : 'Изображение товара Parts.Sotrans'
              "
              loading="lazy"
              width="196"
              height="196"
            />
            <PlugImage v-else />
          </div>
          <div class="product__price-and-title">
            <div class="product-content__price">
              <template v-if="!isDisabledProduct">
                <span>от</span>
                {{ getNumberWithSpaces(product.price) }}&nbsp;₽
              </template>
              <template v-else>
                <p class="product-content__price_no-price">Нет в наличии</p>
              </template>
            </div>
            <div class="product-content__title">{{ product.title }}</div>
          </div>
        </div>

        <div class="product-content__sub">
          <span class="product-content__sub-text brand-name">{{
            product.brand.name
          }}</span>
          <span class="product-content__sub-text">{{ product.article }}</span>
        </div>
        <div
          v-if="product.tags.length"
          class="product-content__tags"
          :class="!withoutButton && 'product-content__tags_margin-bottom'"
        >
          <Badge
            v-for="tag in product.tags"
            :tag="{
              text: tag,
              tagType: 'round',
              tagColor: 'gray',
              size: 'small',
            }"
          />
        </div>
      </div>
      <AppButton
        v-if="!withoutButton"
        class="product-content__link"
        text="Подробнее"
        size="s"
      />
    </div>
  </NuxtLink>
</template>

<style lang="scss" scoped>
.product {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 16px;
  padding: 12px;
  background: $control-bg_white;
  cursor: pointer;
  transition: 0.2s box-shadow;
  @include md-breakpoint {
    padding: 0;
  }

  &.not-available {
    .product {
      &-img__wrapper {
        position: relative;
        background-color: $bg_img-overlay;

        &:before {
          content: "";
          width: 100%;
          height: 100%;
          position: absolute;
          left: 0;
          top: 0;
          background-color: $bg_page;
          opacity: 0.5;
          z-index: 1;
        }
      }

      &-content {
        &__price {
          @include font(20px, 24px, 600);
          color: $content_tretiary;
        }

        &__title,
        &__sub-text,
        .tag {
          color: $content_tretiary;
        }
      }
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 20px;
    flex-grow: 1;
  }

  &__inner-wrapper {
    display: flex;
    flex-direction: column;
    gap: 18px;

    &:not(
        .product_sm .product__inner-wrapper,
        .product_l .product__inner-wrapper
      ) {
      @include xs-breakpoint {
        flex-direction: row;
        gap: 12px;
      }
    }
  }

  &__price-and-title {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &:not(
        .product_sm .product__price-and-title,
        .product_l .product__price-and-title
      ) {
      @include xs-breakpoint {
        flex-direction: column-reverse;
        justify-content: flex-end;
        gap: 4px;
      }
    }
  }

  &-content {
    &__price {
      color: $content_primary;
      @include font(24px, 28px, 600);
      @include md-breakpoint {
        @include font(18px, 24px, 600);
      }
      span {
        @include font(18px, 24px, 600);
        @include md-breakpoint {
          @include font(14px, 20px, 500);
        }
      }
      &_no-price {
        @include font(20px, 24px, 600);
        @include md-breakpoint {
          @include font(16px, 20px, 500);
        }
      }
    }

    &__title {
      transition: color ease 0.3s;
      color: $content_primary;
      word-break: break-word;
      @include font(14px, 20px);
    }

    &__sub {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-top: 4px;

      &:not(
          .product_sm .product-content__sub,
          .product_l .product-content__sub
        ) {
        @include md-breakpoint {
          margin-top: 12px;
          align-items: flex-start;
          gap: 0 8px;
          flex-wrap: wrap;
        }
      }

      &-text {
        color: $content_secondary;
        @include font(14px, 20px);

        &:not(:last-child) {
          position: relative;
          &::after {
            content: "";
            position: absolute;
            width: 4px;
            height: 4px;
            border-radius: 100%;
            background-color: $content_icons;
            right: -10px;
            top: 50%;
            transform: translateY(-50%);
            @include md-breakpoint {
              display: none;
            }
          }
        }
      }
    }

    &__tags {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-top: 12px;
      @include xs-breakpoint {
        margin-top: 8px;
      }
    }

    &__link {
      width: fit-content;
      @include md-breakpoint {
        padding: 8px 12px;
      }
      @include xs-breakpoint {
        width: 100%;
      }
    }
  }

  &-img {
    width: 100%;
    height: 100%;
    mix-blend-mode: multiply;
    object-fit: cover;
    object-position: center;
    @include md-breakpoint {
      object-fit: contain;
    }

    &__wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 304px;
      width: 100%;
      border-radius: 8px;
      background: $bg_img-overlay;
      overflow: hidden;
      flex-shrink: 0;
      @include lg-breakpoint {
        height: 253px;
      }
      @include sm-breakpoint {
        height: 152px;
      }

      &:not(
          .product_sm .product-img__wrapper,
          .product_l .product-img__wrapper
        ) {
        @include xs-breakpoint {
          width: 68px;
          height: 68px;
          flex-shrink: 0;
        }
      }
    }
  }
  @include sm-breakpoint {
    padding: 0;
  }
  @include mf-breakpoint {
    &:hover {
      box-shadow: 0 4px 24px 0 #0000001f;
    }
  }

  &_sm {
    border-radius: 0;
    padding: 0;
    max-width: 196px;
    @include md-breakpoint {
      max-width: 152px;
    }
    @include mf-breakpoint {
      &:hover {
        box-shadow: none;
        .product-content__title {
          color: $content_orange;
        }
      }
    }

    .product {
      &__inner-wrapper {
        gap: 6px;
      }

      &__price-and-title {
        gap: 4px;
      }

      &-img {
        &__wrapper {
          max-height: 196px;
          @include md-breakpoint {
            max-height: 152px;
          }
        }
      }

      &-content {
        &__price {
          @include font(18px, 24px, 600);
          span {
            @include font(14px, 24px, 600);
          }
        }

        &__title {
          @include font(14px, 20px);
          @include txt-overflow(3);
          @include md-breakpoint {
            @include txt-overflow(2);
          }
        }

        &__tags {
          display: none;
        }

        &__sub {
          flex-direction: column;
          align-items: flex-start;
          gap: 0;

          span {
            &:after {
              display: none;
            }
          }
        }
      }
    }
  }

  &_l {
    border-radius: 0;
    padding: 0;
    .product {
      &-content {
        &__tags {
          display: none;
        }

        &__price {
          @include font(18px, 24px, 600);
          span {
            @include font(14px, 24px, 600);
          }
        }

        &__sub {
          flex-direction: column;
          align-items: flex-start;
          gap: 0;

          span {
            &:after {
              display: none;
            }
          }
        }
      }
    }
  }
}
</style>
